/**
 * @fileoverview Defines the main layout structure of the application.
 *
 * The Frame component is the primary screen that organizes the application's
 * UI into a responsive layout, including the header, sidebar, and main content area.
 * It manages the visibility and collapsed state of the sidebar based on screen size
 * and handles interactions between these major UI sections.
 */

import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { FooterSection } from "./sections/FooterSection/FooterSection";
import { MainContentSection } from "./sections/MainContentSection/MainContentSection";
import { useSpinner } from "../../lib/SpinnerContext";

import { Sidebar } from "../../components/Sidebar";
import { HeaderSection } from "./sections/HeaderSection/HeaderSection";
import { LanguageSelectorModal } from '../../components/LanguageSelectorModal';
import { ShareModal } from '../../components/modals/ShareModal';
import { useLanguage } from '../../lib/LanguageContext';
import AdBlockerNotice from '../../components/ads/AdBlockerNotice';
import { SpinResult, GameType } from '../../types';
import { SidebarMode } from "../../lib/MenuItems";
import { useLocation } from 'react-router-dom';
import { spinnerService } from '../../services/spinnerService';
import { useParticipants } from '../../lib/ParticipantsContext';
import { useSettings } from '../../lib/SettingsContext';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { saveUserResult, getUserResults } from '../../services/spinnerService';
import { useGame } from '../../lib/GameContext';
import AdManager from '../../components/ads/AdManager';
import { Helmet } from 'react-helmet-async';

/**
 * A placeholder component for the mobile menu.
 * This will be replaced with the actual menu controls in the future.
 */
const MobileMenuContentPlaceholder = () => (
  <div>
    {/* Placeholder for ad */}
  </div>
);

/**
 * The main application Frame component.
 * It is responsible for the overall page layout and orchestrates the interactions
 * between the Header, Sidebar, and MainContent sections. It handles responsive
 * design by adjusting the layout for mobile, tablet, and desktop views.
 * @returns {JSX.Element} The rendered Frame component.
 */
export const Frame = (): JSX.Element => {

  const [spinnerName, setSpinnerName] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Tablet sidebar starts collapsed
  const [isSidebarVisible, setIsSidebarVisible] = useState(false); // For mobile overlay
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 1024; // Tablets now use mobile behavior
  const isDesktop = windowWidth >= 1024;
  const sidebarRef = useRef<any>(null);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { setLanguage } = useLanguage();
  const [spinResults, setSpinResults] = useState<SpinResult[]>([]);
  const [mode, setMode] = useState<SidebarMode>('participants');
  const [activeMenuMode, setActiveMenuMode] = useState<SidebarMode>('participants');
  
  // DEBUG: Track addSpinResult calls to identify patterns
  const [lastAddSpinResultTime, setLastAddSpinResultTime] = useState<number>(0);
  const [addSpinResultCallCount, setAddSpinResultCallCount] = useState<number>(0);
  const location = useLocation();
  const spinnerId = location.state?.spinnerId;
  const { setParticipants } = useParticipants();
  const { setSelectedSpinner, spinnerStyles } = useSpinner();
  const { setAudioEnabled } = useSettings();
  const [showShareModal, setShowShareModal] = useState(false);
  const [sidebarSettings, setSidebarSettings] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();
  const { currentGameType } = useGame();

  /**
   * Load results on app load or when user changes
   */
  useEffect(() => {
    const loadResults = async () => {
      if (user) {
        // Check if migration was already done for this user
        const migrationKey = `migrationDone_${user.uid}`;
        const hasMigrated = localStorage.getItem(migrationKey) === 'true';
        
        // Clear any cached results that might be causing fake results
        const cacheKey = `fakeResultsCleared_${user.uid}`;
        const fakeResultsCleared = localStorage.getItem(cacheKey) === 'true';
        
        if (!fakeResultsCleared) {
          localStorage.removeItem('spinResults');
          localStorage.setItem(cacheKey, 'true');
        }
        
        // Load from Firestore
        const results = await getUserResults(user.uid);
        
        // Check for localStorage results to migrate (only once per user)
        if (!hasMigrated) {
          const local = localStorage.getItem('spinResults');
          if (local) {
            try {
              const parsed = JSON.parse(local);
              const localResults = Array.isArray(parsed) ? parsed.map((r: any) => ({ ...r, type: r.type as SidebarMode })) : [];
              
              if (localResults.length > 0) {
                // Migrate localStorage results to Firestore
                for (const localResult of localResults) {
                  await saveUserResult(user.uid, localResult);
                }
                
                // Clear localStorage after migration
                localStorage.removeItem('spinResults');
                
                // Mark migration as done for this user
                localStorage.setItem(migrationKey, 'true');
                
                // Reload results from Firestore (now includes migrated ones)
                const updatedResults = await getUserResults(user.uid);
                setSpinResults(updatedResults.map(r => ({ ...r, type: r.type as SidebarMode })) as SpinResult[]);
                setMigrationDone(true);
                return;
              }
            } catch (error) {
              // Failed to migrate localStorage results
            }
          }
          
          // Mark migration as done even if no data to migrate
          localStorage.setItem(migrationKey, 'true');
          setMigrationDone(true);
        }
        
        setSpinResults(results.map(r => ({ ...r, type: r.type as SidebarMode })) as SpinResult[]);
      } else {
        // Load from localStorage
        const local = localStorage.getItem('spinResults');
        if (local) {
          try {
            const parsed = JSON.parse(local);
            const results = Array.isArray(parsed) ? parsed.map((r: any) => ({ ...r, type: r.type as SidebarMode })) : [];
            setSpinResults(results);
          } catch { 
            setSpinResults([]);
          }
        } else {
          setSpinResults([]);
        }
      }
    };
    loadResults();
  }, [user]);

  /**
   * Save result on spin
   */
  const addSpinResult = async (winner: string, type: SidebarMode, spinnerName: string, gameType?: string, gameResultCandidate?: string) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastAddSpinResultTime;
    
    // Prevent duplicate calls within 5 seconds
    if (timeSinceLastCall < 5000) {
      return;
    }
    
    // Block 10-second patterns (likely fake results)
    if (timeSinceLastCall > 0 && Math.abs(timeSinceLastCall - 10000) < 2000) {
      return;
    }
    
    setAddSpinResultCallCount(prev => prev + 1);
    setLastAddSpinResultTime(now);
    
    // Only include gameResultCandidate if it has a value (not undefined)
    const resultData: any = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${Math.random().toString(36).substr(2, 5)}`,
      winner,
      type,
      timestamp: Date.now(),
      spinnerName,
      gameType: gameType as GameType | undefined,
    };
    
    // Only add gameResultCandidate if it's not undefined
    if (gameResultCandidate !== undefined) {
      resultData.gameResultCandidate = gameResultCandidate;
    }
    
    const result: SpinResult = resultData;
    setSpinResults(prev => [result, ...prev]);
    if (user) {
      await saveUserResult(user.uid, result);
    } else {
      // Save to localStorage
      const prev = localStorage.getItem('spinResults');
      let arr = [];
      if (prev) {
        try {
          arr = JSON.parse(prev);
          if (!Array.isArray(arr)) arr = [];
        } catch { arr = []; }
      }
      arr.unshift(result);
      localStorage.setItem('spinResults', JSON.stringify(arr));
    }
  };

  /**
   * Handles the share button click in the header.
   * It retrieves the settings from the Sidebar component via its ref.
   */
  const handleShareClick = () => {
    if (sidebarRef.current) {
      const settings = sidebarRef.current.getSettings();
      setSidebarSettings(settings);
    }
    setShowShareModal(true);
  };

  /**
   * Handles sharing a saved spinner from the SavedSpinnersModal.
   * Opens the spinner for sharing (shared links will create copies for users).
   */
  const handleSavedSpinnerShare = async (spinnerId: string) => {
    try {
      const spinnerData = await spinnerService.getSpinner(spinnerId);
      
      if (spinnerData) {
        // Simply open the share modal with the current spinner
        // Shared links will automatically create copies for users
        const settings = {
          mode: 'participants',
          participants: spinnerData.participants,
          style: spinnerData.style,
          language: spinnerData.language,
          theme: spinnerData.theme,
          sound: spinnerData.sound,
          spinTime: spinnerData.spinTime,
          name: spinnerData.name || 'Untitled Spinner',
        };
        setSidebarSettings(settings);
        setShowShareModal(true);
      } else {
        toast.error('Saved spinner not found');
      }
    } catch (error) {
      toast.error('Error loading saved spinner');
    }
  };

  /**
   * Handles login requests from the saved spinners modal.
   * Opens the login modal when user clicks login from saved spinners.
   */
  const handleLoginRequest = () => {
    // This will be handled by the HeaderSection which already has login modal functionality
    // We can add additional logic here if needed
  };



  // Track if migration has been done to prevent duplicates
  const [migrationDone, setMigrationDone] = useState(false);

  /**
   * Handles opening a saved spinner from the SavedSpinnersModal.
   * Loads the spinner data and applies it to the app (like opening a shared spinner).
   */
  const handleSavedSpinnerOpen = async (spinnerId: string) => {
    try {
      const data = await spinnerService.getSpinner(spinnerId);
      if (data) {
        // Set participants first
        const sharedParticipants = data.participants.map((name, idx) => ({ 
          id: String(idx), 
          name 
        }));
        setParticipants(sharedParticipants);
        // Apply settings to sidebar
        const settings = {
          mode: 'participants',
          participants: data.participants,
        };
        setTimeout(() => {
          if (sidebarRef.current && sidebarRef.current.setSettings) {
            sidebarRef.current.setSettings(settings);
          }
        }, 50);
        // Set other app settings
        const found = spinnerStyles.find(s => s.id === data.style);
        if (found) {
          setSelectedSpinner(found);
        } else {
          setSelectedSpinner(spinnerStyles[0]);
        }
        setLanguage(data.language);
        setAudioEnabled(data.sound);
        if (data.theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        console.log('Setting spinnerName from Firestore:', data.name);
        setSpinnerName(data.name || '');
      }
    } catch (error) {
      toast.error('Error loading saved spinner');
    }
  };

  // Effect to handle window resizing for responsive layout changes.
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ensure sidebar is always expanded on desktop and collapsed on mobile/tablet.
  useEffect(() => {
    if (isDesktop) {
      setSidebarCollapsed(false);
    } else {
      setSidebarCollapsed(true);
    }
  }, [isDesktop]);

  // Simplified initialization effect - runs only once on app load
  useEffect(() => {
    if (isInitialized) return;

    const initializeApp = async () => {
      try {
        if (spinnerId) {
          spinnerService.getSpinner(spinnerId).then(data => {
            if (data) {
              setSpinnerName(data.name || '');
              // Initialize all settings at once
              const settings = {
                mode: 'participants', // Default to participants mode for old spinners
                participants: data.participants,
              };
              
              // Set participants first
              const sharedParticipants = data.participants.map((name, idx) => ({ 
                id: String(idx), 
                name 
              }));
              setParticipants(sharedParticipants);
              
              // Apply all settings to sidebar with a small delay to ensure it's mounted
              setTimeout(() => {
                if (sidebarRef.current && sidebarRef.current.setSettings) {
                  sidebarRef.current.setSettings(settings);
                } else {
                  setTimeout(() => {
                    if (sidebarRef.current && sidebarRef.current.setSettings) {
                      sidebarRef.current.setSettings(settings);
                    }
                  }, 100);
                }
              }, 50);
              
              // Set other app settings
              const found = spinnerStyles.find(s => s.id === data.style);
              if (found) {
                setSelectedSpinner(found);
              } else {
                setSelectedSpinner(spinnerStyles[0]);
              }
              
              setLanguage(data.language);
              setAudioEnabled(data.sound);
              
              if (data.theme === 'dark') {
                document.documentElement.classList.add('dark');
              } else {
                document.documentElement.classList.remove('dark');
              }
              
              // Note: Mode will be set by the setSettings function
            } else {
              setMode('participants');
            }
          });
        } else {
          // Load default configuration
          setMode('participants');
        }
        
        setIsInitialized(true);
      } catch (error) {
        // Fallback to default
        setMode('participants');
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, [spinnerId, isInitialized]); // Removed other dependencies that might cause re-runs

  useEffect(() => {
    if (!['spinner-styles', 'games'].includes(mode)) {
      setActiveMenuMode(mode);
    }
  }, [mode]);

  /**
   * Callback function passed to the sidebar to update the frame's state
   * when the sidebar's collapsed state changes on desktop/tablet.
   * @param {boolean} isNowCollapsed - The new collapsed state of the sidebar.
   */
  const handleSidebarCollapseChange = useCallback((isNowCollapsed: boolean) => {
    setSidebarCollapsed(isNowCollapsed);
  }, []);

  // Stable callbacks for mobile close
  const handleMobileClose = useCallback(() => {
    setIsSidebarVisible(false);
  }, []);

  // Memoized sidebar props to prevent unnecessary re-renders
  const sidebarProps = useMemo(() => ({
    ref: sidebarRef,
    // Desktop props
    isCollapsed: isDesktop ? sidebarCollapsed : undefined,
    setIsCollapsed: isDesktop ? setSidebarCollapsed : undefined,
    onCollapseChange: isDesktop ? handleSidebarCollapseChange : undefined,
    // Mobile props
    mobileMode: isMobile,
    mobileVisible: isMobile ? isSidebarVisible : undefined,
    onClose: isMobile ? handleMobileClose : undefined,
    // Common props
    spinResults,
    mode,
    setMode,
    onOpenShareModal: (settings: any) => {
      setSidebarSettings(settings);
      setShowShareModal(true);
    },
    onBinaryListUpdate: (binaryList: string[]) => {
      // This will be used to update the wheel items when binary list changes
      if (mode === 'binary') {
        const binaryParticipants = binaryList.map((name, index) => ({
          id: `binary-${index}`,
          name
        }));
        // We'll need to pass this to MainContentSection
      }
    },
  }), [
    sidebarRef,
    isDesktop,
    sidebarCollapsed,
    setSidebarCollapsed,
    isMobile,
    isSidebarVisible,
    handleSidebarCollapseChange,
    handleMobileClose,
    spinResults,
    mode,
    setMode
  ]);

  // Dynamically calculate sidebar width for adjusting main content margin and width.
  let sidebarWidth = 0;
  if (isMobile) {
    sidebarWidth = 0; // No sidebar visible on mobile, it's an overlay.
  } else { // Desktop and Tablet
    sidebarWidth = sidebarCollapsed ? 80 : 256;
  }

  // Style object to dynamically adjust the main content area's position and size.
  const mainContentStyle = {
    width: `calc(100vw - ${sidebarWidth}px)`,
    maxWidth: '100vw',
    marginLeft: sidebarWidth,
    transition: isMobile ? 'none' : 'width 0.3s, margin-left 0.3s',
    // marginBottom: '48px', // Removed to prevent extra space
  };

  // Prevent body scrolling when Frame is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const getEffectiveMode = () => {
    if (mode === 'games') {
      if (currentGameType === 'just-spinner') {
        return activeMenuMode;
      } else {
        return 'participants';
      }
    }
    return activeMenuMode;
  };

  // Determine the current canonical URL based on the pathname
  const canonicalUrl = useMemo(() => {
    const pathname = location.pathname;
    // Remove trailing slash unless it's the root path
    const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    return `https://spinnergames.fun${cleanPath}`;
  }, [location.pathname]);

  return (
    <div className="app-root flex flex-col w-full bg-white dark:bg-gray-900" style={{ overflow: 'hidden', height: '100vh' }}>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
      {/* Header is always present and fixed by its own CSS */}
      <HeaderSection 
        onMobileMenuClick={() => setIsSidebarVisible(true)}
        showLanguageModal={showLanguageModal}
        setShowLanguageModal={setShowLanguageModal}
        isMobile={isMobile}
        onShareClick={handleShareClick}
        onSavedSpinnerShare={handleSavedSpinnerShare}
        onLoginRequest={handleLoginRequest}
        onSavedSpinnerOpen={handleSavedSpinnerOpen}
      />

      <div className="main-content-area flex flex-1" style={{ minHeight: 0 }}>
        {/* Single responsive Sidebar component */}
        <Sidebar 
          {...sidebarProps}
        />

        {/* Mobile overlay backdrop */}
        {isMobile && (
          <div
            className={`fixed inset-0 z-30 bg-black transition-opacity duration-300 ease-in-out ${isSidebarVisible ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'}`}
            onClick={() => setIsSidebarVisible(false)}
          />
        )}

        {/* Main Content Area - scrollable */}
        <div 
          className="main-content flex flex-col flex-1"
          style={{ ...mainContentStyle, overflowY: 'auto', overflowX: 'hidden', paddingTop: '54px' }}
        >
          <div className="main-content-wrapper flex-grow flex flex-col spinner-container" style={{ position: 'relative' }}>
            <MainContentSection sidebarCollapsed={sidebarCollapsed} isSmallScreen={isMobile} sidebarRef={isDesktop ? undefined : sidebarRef} addSpinResult={addSpinResult} mode={getEffectiveMode()} sidebarMode={mode} spinnerName={spinnerName} setSpinnerName={setSpinnerName} />
            {isMobile && <MobileMenuContentPlaceholder />} {/* Show menu placeholder below content on mobile */}
            <FooterSection />
          </div>
        </div>
      </div>
      
      {/* Modal for selecting application language. */}
      <LanguageSelectorModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        onSelect={(langCode) => {
          setLanguage(langCode);
          setShowLanguageModal(false);
        }}
      />
      {/* Modal for sharing the current spinner configuration. */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        sidebarSettings={sidebarSettings}
        spinnerName={spinnerName}
        setSpinnerName={setSpinnerName}
      />

      {/* Ad Blocker Notice */}
      <AdBlockerNotice />


    </div>
  );
};
